import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Search, Package, Network, X, Layers } from 'lucide-react';
import { PageHeader, KpiCard, STREAMS } from '../components/PageHeader';
import { toast } from 'sonner';
import { BoxListEditor } from '../components/products/BoxListEditor';

const ProductTypesPage = ({ level: lockedLevel, initialTab = 'types' }) => {
  const { api } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  // Product type dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    product_type_name: '',
    description: '',
    default_bom_variant: '',
    developed_by: '',
    next_revision_date: '',
    notes: '',
    active_status: true,
    level: lockedLevel || 'product',
    item_sku: '',
    product_sku: '',
    sales_price: '',
    box_items: [],
    option_groups: [],
    bundle_pricing: false,
  });
  const [allItems, setAllItems] = useState([]);
  const [levelFilter, setLevelFilter] = useState(lockedLevel || 'all');
  useEffect(() => { setLevelFilter(lockedLevel || 'all'); }, [lockedLevel]);

  // Family dialog
  const [famDialogOpen, setFamDialogOpen] = useState(false);
  const [editingFam, setEditingFam] = useState(null);
  const [famSearch, setFamSearch] = useState('');
  const [famForm, setFamForm] = useState({
    family_name: '',
    description: '',
    member_product_type_ids: [],
    notes: '',
    active: true,
  });

  const fetchData = async () => {
    try {
      const [ptRes, famRes] = await Promise.all([
        api().get('/product-types'),
        api().get('/product-families').catch(() => ({ data: [] })),
      ]);
      api().get('/items').then(r => setAllItems(r.data || [])).catch(() => {});
      setProductTypes(ptRes.data);
      setFamilies(famRes.data);
    } catch (error) {
      toast.error('Failed to fetch product types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ===== Product Type =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    const kit = (rows) => (rows || []).filter(r => r.item_sku).map(r => ({ item_sku: r.item_sku, quantity: Number(r.quantity) || 1 }));
    const body = {
      ...formData,
      sales_price: formData.sales_price === '' || formData.sales_price === null ? null : parseFloat(formData.sales_price),
      box_items: kit(formData.box_items),
      option_groups: (formData.option_groups || []).map(g => ({ name: g.name, required: g.required !== false, choices: kit(g.choices) })).filter(g => g.name.trim() && g.choices.length),
    };
    try {
      if (editingItem) {
        await api().put(`/product-types/${editingItem.id}`, body);
        toast.success('Product type updated');
      } else {
        await api().post('/product-types', body);
        toast.success('Product type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      product_type_name: item.product_type_name,
      description: item.description || '',
      default_bom_variant: item.default_bom_variant || '',
      developed_by: item.developed_by || '',
      next_revision_date: item.next_revision_date || '',
      notes: item.notes || '',
      active_status: item.active_status,
      level: item.level || 'product',
      item_sku: item.item_sku || '',
      product_sku: item.product_sku || '',
      sales_price: item.sales_price ?? '',
      box_items: item.box_items || [],
      option_groups: item.option_groups || [],
      bundle_pricing: !!item.bundle_pricing,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product type?')) return;
    try {
      await api().delete(`/product-types/${id}`);
      toast.success('Product type deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      product_type_name: '', description: '', default_bom_variant: '',
      developed_by: '', next_revision_date: '', notes: '', active_status: true, level: lockedLevel || 'product', item_sku: '', product_sku: '', sales_price: '', box_items: [], option_groups: [], bundle_pricing: false,
    });
  };

  // ===== Family =====
  const resetFamForm = () => {
    setEditingFam(null);
    setFamForm({ family_name: '', description: '', member_product_type_ids: [], notes: '', active: true });
  };

  const openFamCreate = () => { resetFamForm(); setFamDialogOpen(true); };
  const openFamEdit = (fam) => {
    setEditingFam(fam);
    setFamForm({
      family_name: fam.family_name || '',
      description: fam.description || '',
      member_product_type_ids: fam.member_product_type_ids || [],
      notes: fam.notes || '',
      active: fam.active !== false,
    });
    setFamDialogOpen(true);
  };

  const toggleMember = (ptId) => {
    setFamForm(f => {
      const has = f.member_product_type_ids.includes(ptId);
      return { ...f, member_product_type_ids: has ? f.member_product_type_ids.filter(x => x !== ptId) : [...f.member_product_type_ids, ptId] };
    });
  };

  const saveFamily = async (e) => {
    e?.preventDefault?.();
    if (!famForm.family_name.trim()) { toast.error('Family name is required'); return; }
    if (famForm.member_product_type_ids.length === 0) { toast.error('Select at least one member product'); return; }
    try {
      if (editingFam) {
        await api().put(`/product-families/${editingFam.id}`, famForm);
        toast.success('Family updated');
      } else {
        await api().post('/product-families', famForm);
        toast.success('Family created');
      }
      setFamDialogOpen(false);
      resetFamForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const deleteFamily = async (id) => {
    if (!window.confirm('Delete this product family?')) return;
    try {
      await api().delete(`/product-families/${id}`);
      toast.success('Family deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Filtering
  const levelOf = (item) => item.level || 'product';
  const productsOnly = productTypes.filter(pt => levelOf(pt) === 'product');
  const filteredItems = productTypes.filter(item =>
    (levelFilter === 'all' || levelOf(item) === levelFilter) && (
    item.product_type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.item_sku || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredFamilies = families.filter(f =>
    f.family_name.toLowerCase().includes(famSearch.toLowerCase()) ||
    f.description?.toLowerCase().includes(famSearch.toLowerCase())
  );
  const ptMap = productTypes.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

  return (
    <div className="space-y-6 animate-slideIn" data-testid="product-types-page">
      <PageHeader
        crumbs={[{ label: 'Build' }, lockedLevel ? { label: STREAMS[lockedLevel].label, to: `${STREAMS[lockedLevel].base}` } : { label: 'Catalog' }, { label: activeTab === 'families' ? 'Families' : 'Catalog' }]}
        title={activeTab === 'families' ? 'Product Families' : lockedLevel === 'assembly' ? 'Assembly Catalog' : lockedLevel === 'product' ? 'Product Catalog' : 'Products & Assemblies'}
        icon={activeTab === 'families' ? Network : lockedLevel === 'assembly' ? Layers : Package}
        stream={lockedLevel || 'neutral'}
        badge={lockedLevel ? `${STREAMS[lockedLevel].singular} stream` : null}
        description={activeTab === 'families' ? 'A family groups related products, e.g. every model in a product line.'
          : lockedLevel === 'assembly' ? 'Assemblies are stocked items built from parts (and other assemblies). Use their SKU as a line in product or parent-assembly BOMs.'
          : lockedLevel === 'product' ? 'Products combine assemblies and loose parts. Group them into families.'
          : 'Family → Products → Assemblies → Parts.'}
        kpis={activeTab === 'types' ? <>
          <KpiCard stream={lockedLevel || 'neutral'} label={lockedLevel === 'assembly' ? 'Assemblies' : lockedLevel === 'product' ? 'Products' : 'Total'} value={filteredItems.length} testId="catalog-kpi-count" />
          <KpiCard stream={lockedLevel || 'neutral'} label="Active" value={filteredItems.filter(i => i.active_status !== false).length} testId="catalog-kpi-active" />
          {lockedLevel !== 'assembly' && <KpiCard stream={lockedLevel || 'neutral'} label="Families" value={families.length} testId="catalog-kpi-families" />}
        </> : null}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {lockedLevel !== 'assembly' && <TabsList>
          <TabsTrigger value="types" data-testid="product-types-tab">
            <Package className="w-4 h-4 mr-2" /> {lockedLevel === 'product' ? 'Products' : 'Products & Assemblies'}
          </TabsTrigger>
          <TabsTrigger value="families" data-testid="product-families-tab">
            <Network className="w-4 h-4 mr-2" /> Product Families
          </TabsTrigger>
        </TabsList>}

        {/* Product Types tab */}
        <TabsContent value="types" className="space-y-4">
          <div className="flex items-center justify-between">
            {lockedLevel ? <span /> : <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid="level-filter">
              {[['all', 'All'], ['product', 'Products'], ['assembly', 'Assemblies']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setLevelFilter(k)} className={`px-3 py-1.5 text-sm transition-colors ${levelFilter === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`level-filter-${k}`}>
                  {label} <span className="opacity-60">· {k === 'all' ? productTypes.length : productTypes.filter(pt => levelOf(pt) === k).length}</span>
                </button>
              ))}
            </div>}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-product-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {lockedLevel === 'assembly' ? 'Add Assembly' : lockedLevel === 'product' ? 'Add Product' : 'Add Product / Assembly'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? `Edit ${formData.level === 'assembly' ? 'Assembly' : 'Product'}` : 'Add Product / Assembly'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Level *</Label>
                    <div className={`grid grid-cols-2 gap-2 ${lockedLevel && !editingItem ? 'pointer-events-none opacity-60' : ''}`} data-testid="product-type-level-picker">
                      {[['product', 'Product', 'Sold / shipped. BOM = assemblies + loose parts'], ['assembly', 'Assembly', 'Built from parts, stocked, used in products']].map(([k, label, hint]) => (
                        <button key={k} type="button" onClick={() => setFormData({ ...formData, level: k })} className={`text-left rounded-sm border p-2 transition-colors ${formData.level === k ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`product-type-level-${k}`}>
                          <div className="text-sm font-medium text-slate-800">{label}</div>
                          <div className="text-[11px] text-slate-500">{hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {formData.level === 'assembly' && (
                    <div className="space-y-2">
                      <Label>Assembly SKU (Item Master) *</Label>
                      <Input value={formData.item_sku} onChange={(e) => setFormData({ ...formData, item_sku: e.target.value.toUpperCase() })} placeholder="e.g., ASM-FRAME-01" required className="rounded-sm mono" data-testid="product-type-item-sku-input" />
                      <p className="text-[11px] text-slate-500">A stock item with this SKU is created automatically if it doesn't exist. Use this SKU as a line in product or parent-assembly BOMs.</p>
                    </div>
                  )}
                  {formData.level !== 'assembly' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Product SKU</Label>
                        <Input value={formData.product_sku} onChange={(e) => setFormData({ ...formData, product_sku: e.target.value.toUpperCase() })} placeholder="e.g., FD-11FT" className="rounded-sm mono" data-testid="product-type-product-sku-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Selling price</Label>
                        <Input type="number" min="0" step="0.01" value={formData.sales_price} onChange={(e) => setFormData({ ...formData, sales_price: e.target.value })} placeholder="0.00" className="rounded-sm mono" data-testid="product-type-sales-price-input" />
                      </div>
                      <p className="col-span-2 text-[11px] text-slate-500">Prints on invoices and fills in automatically on sales orders. SKU must not match any other product or inventory item.</p>
                    </div>
                  )}
                  {formData.level !== 'assembly' && (
                    <BoxListEditor boxItems={formData.box_items || []} optionGroups={formData.option_groups || []} bundlePricing={formData.bundle_pricing} items={allItems}
                      onChange={(patch) => setFormData(f => ({ ...f, ...patch }))} />
                  )}
                  <div className="space-y-2">
                    <Label>{formData.level === 'assembly' ? 'Assembly' : 'Product'} Name *</Label>
                    <Input value={formData.product_type_name} onChange={(e) => setFormData({ ...formData, product_type_name: e.target.value })} placeholder="e.g., Widget Model A" required className="rounded-sm" data-testid="product-type-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Product description..." className="rounded-sm" data-testid="product-type-description-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Default BOM Variant</Label>
                    <Input value={formData.default_bom_variant} onChange={(e) => setFormData({ ...formData, default_bom_variant: e.target.value })} placeholder="e.g., STD-001" className="rounded-sm" data-testid="product-type-bom-variant-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Developed By</Label>
                      <Input value={formData.developed_by} onChange={(e) => setFormData({ ...formData, developed_by: e.target.value })} placeholder="Author / engineer name" className="rounded-sm" data-testid="product-type-developed-by-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Next Revision Date</Label>
                      <Input type="date" value={formData.next_revision_date || ''} onChange={(e) => setFormData({ ...formData, next_revision_date: e.target.value })} className="rounded-sm mono" data-testid="product-type-next-revision-date-input" />
                    </div>
                  </div>
                  {editingItem?.created_at && (
                    <div className="text-xs text-slate-500" data-testid="product-type-created-at-meta">
                      Created {new Date(editingItem.created_at).toLocaleString()}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes about this product type…" className="rounded-sm" data-testid="product-type-notes-input" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Active Status</Label>
                    <Switch checked={formData.active_status} onCheckedChange={(checked) => setFormData({ ...formData, active_status: checked })} data-testid="product-type-active-switch" />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="product-type-submit-btn">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search product types..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-sm" data-testid="product-type-search-input" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48"><div className="spinner" /></div>
              ) : filteredItems.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Default BOM</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.id} data-testid={`product-type-row-${item.id}`}>
                        <TableCell className="font-medium">{item.product_type_name}</TableCell>
                        <TableCell data-testid={`product-type-level-badge-${item.id}`}>
                          {levelOf(item) === 'assembly'
                            ? <span className="text-[11px] px-2 py-0.5 rounded-sm bg-sky-50 text-sky-700 border border-sky-200">Assembly · <span className="mono">{item.item_sku}</span></span>
                            : <span className="text-[11px] px-2 py-0.5 rounded-sm bg-orange-50 text-orange-700 border border-orange-200">Product{item.product_sku && <> · <span className="mono">{item.product_sku}</span></>}</span>}
                          {levelOf(item) !== 'assembly' && item.sales_price != null && <span className="ml-2 text-xs mono text-slate-600" data-testid={`product-type-price-${item.id}`}>${Number(item.sales_price).toFixed(2)}</span>}
                        </TableCell>
                        <TableCell className="text-slate-500 max-w-xs truncate">{item.description || '-'}</TableCell>
                        <TableCell className="mono text-sm">{item.default_bom_variant || '-'}</TableCell>
                        <TableCell>
                          <span className={`status-badge ${item.active_status ? 'status-released' : 'status-obsolete'}`}>{item.active_status ? 'Active' : 'Inactive'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-product-type-${item.id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-product-type-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <Package className="w-12 h-12 mb-3 text-slate-300" />
                  <p>No product types found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Product Families tab */}
        <TabsContent value="families" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">A family groups related products (e.g. every model in a product line). Only Products can be family members — assemblies belong inside product BOMs.</p>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={openFamCreate} data-testid="add-product-family-btn">
              <Plus className="w-4 h-4 mr-2" /> New Family
            </Button>
          </div>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search families..." value={famSearch} onChange={(e) => setFamSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="product-family-search-input" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredFamilies.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <Network className="w-12 h-12 mb-3 text-slate-300" />
                  <p>No product families yet</p>
                  <Button variant="link" onClick={openFamCreate} className="text-orange-600">Create your first family</Button>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Family</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFamilies.map(fam => (
                      <TableRow key={fam.id} data-testid={`product-family-row-${fam.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{fam.family_name}</p>
                            {fam.description && <p className="text-xs text-slate-500">{fam.description}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(fam.member_product_type_ids || []).map(mid => (
                              <Badge key={mid} variant="outline" className="text-xs">{ptMap[mid]?.product_type_name || '—'}</Badge>
                            ))}
                            {(fam.member_product_type_ids || []).length === 0 && <span className="text-xs text-slate-400">No members</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`status-badge ${fam.active ? 'status-released' : 'status-obsolete'}`}>{fam.active ? 'Active' : 'Inactive'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openFamEdit(fam)} data-testid={`edit-product-family-${fam.id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteFamily(fam.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-product-family-${fam.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Family Dialog */}
      <Dialog open={famDialogOpen} onOpenChange={(o) => { setFamDialogOpen(o); if (!o) resetFamForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFam ? 'Edit Family' : 'New Product Family'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveFamily} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Family Name *</Label>
                <Input value={famForm.family_name} onChange={e => setFamForm({ ...famForm, family_name: e.target.value })} placeholder="e.g., 5HT Heat Pump" data-testid="product-family-name-input" required />
              </div>
              <div className="space-y-2 flex items-end justify-end">
                <div className="flex items-center gap-2 border border-slate-200 rounded-sm px-3 py-1.5 w-full justify-between">
                  <Label>Active</Label>
                  <Switch checked={famForm.active} onCheckedChange={v => setFamForm({ ...famForm, active: v })} />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={famForm.description} onChange={e => setFamForm({ ...famForm, description: e.target.value })} placeholder="What does this family represent?" rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Member Product Types *</Label>
              <p className="text-xs text-slate-500">Each selected type becomes a required slot in the final-assembly checklist.</p>
              <div className="border border-slate-200 rounded-sm p-2 max-h-64 overflow-y-auto space-y-1">
                {productTypes.length === 0 && <p className="text-xs text-slate-400 p-2">No product types defined yet.</p>}
                {productsOnly.map(pt => {
                  const checked = famForm.member_product_type_ids.includes(pt.id);
                  return (
                    <button type="button" key={pt.id}
                      onClick={() => toggleMember(pt.id)}
                      className={`w-full text-left flex items-center justify-between rounded-sm px-3 py-1.5 text-sm border ${checked ? 'bg-emerald-50 border-emerald-300' : 'border-transparent hover:bg-slate-50'}`}
                      data-testid={`family-member-toggle-${pt.id}`}
                    >
                      <span>{pt.product_type_name}</span>
                      {checked ? <Badge className="bg-emerald-600">Included</Badge> : <span className="text-xs text-slate-400">Click to add</span>}
                    </button>
                  );
                })}
              </div>
              {famForm.member_product_type_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {famForm.member_product_type_ids.map(mid => (
                    <Badge key={mid} variant="outline" className="text-xs flex items-center gap-1">
                      {ptMap[mid]?.product_type_name || mid}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => toggleMember(mid)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={famForm.notes} onChange={e => setFamForm({ ...famForm, notes: e.target.value })} placeholder="Internal notes…" rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFamDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="product-family-save-btn">
                {editingFam ? 'Save Changes' : 'Create Family'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductTypesPage;
